import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Book,
  Bell,
  ChevronDown,
  ChevronUp,
  Clipboard,
  Mail,
  MessageSquare,
  Bell as BellIcon,
  ChevronLeft,
  Edit,
  Trash2,
  AlertTriangle,
  Download,
  Library
} from 'lucide-react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import LibraryBuilderModal from './LibraryBuilder';
import LibraryImportModal from './LibraryImport';
import surveyService from '../../services/surveyService';
import reminderService from '../../services/reminderService';
import studyBuilderService from '../../services/studyBuilderService';

// A helper to parse + sanitize Markdown so we can render HTML safely
const parseMarkdown = (markdown = '') => {
  const rawHtml = marked(markdown);
  return DOMPurify.sanitize(rawHtml);
};

const StudyLibraryDashboard = () => {
  const navigate = useNavigate();
  const { studyName } = useParams();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [currentStudyId, setCurrentStudyId] = useState(null);
  const [currentStudyDraftId, setCurrentStudyDraftId] = useState(null);

  // Lists for each library item type:
  const [surveys, setSurveys] = useState([]);
  const [missingForms, setMissingForms] = useState([]);
  const [emailReminders, setEmailReminders] = useState([]);
  const [pushReminders, setPushReminders] = useState([]);

  // For toggling expansions (accordion-style)
  const [expandedStandardSurvey, setExpandedStandardSurvey] = useState(null);
  const [expandedMissingForm, setExpandedMissingForm] = useState(null);
  const [expandedReminder, setExpandedReminder] = useState(null);

  // Loading/error states for different data sets
  const [loading, setLoading] = useState({
    surveys: true,
    missingForms: true,
    emailReminders: true,
    pushReminders: true,
    studyDraft: false
  });
  const [error, setError] = useState({
    surveys: null,
    missingForms: null,
    emailReminders: null,
    pushReminders: null,
    studyDraft: null
  });

  // For editing/deleting surveys
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);

  // For editing/deleting reminders
  const [editingReminder, setEditingReminder] = useState(null);
  const [deleteReminderModalOpen, setDeleteReminderModalOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);

  // State for import modal
  const [importModalOpen, setImportModalOpen] = useState(false);

  // On mount, load or re-load IDs from localStorage.
  useEffect(() => {
    updateCurrentIds();
  }, []);

  // After we have a study name, fetch standard & missing surveys
  useEffect(() => {
    if (studyName) {
      fetchSurveys();
      fetchMissingForms();
      
      // Also fetch the study draft details to ensure we have the correct draft ID
      studyBuilderService.getStudyDraftByName(studyName)
        .then(draft => {
          if (draft) {
            // Use draft.study for the actual study ID
            if (draft.study) {
              setCurrentStudyId(draft.study);
              localStorage.setItem('currentStudyId', draft.study.toString());
            }
            // Use draft.id for the draft ID
            if (draft.id) {
              setCurrentStudyDraftId(draft.id);
              localStorage.setItem('currentStudyDraftId', draft.id.toString());
            }
          }
        })
        .catch(err => {
          console.error('Error fetching study draft:', err);
          setError(prev => ({ ...prev, studyDraft: 'Failed to load study draft' }));
        });
    }
  }, [studyName]);

  // After we know the draft ID, fetch reminders
  useEffect(() => {
    if (currentStudyDraftId) {
      console.log('Fetching reminders for draft ID:', currentStudyDraftId);
      fetchReminders();
    }
  }, [currentStudyDraftId]);

  // Cleanup effect to clear localStorage and trigger refresh when component unmounts
  useEffect(() => {
    return () => {
      // Don't clear these values as they're needed for navigation between study builder pages
      // localStorage.removeItem('currentStudyId');
      // localStorage.removeItem('currentStudyDraftId');
      localStorage.removeItem('studyDrafts'); // Still clear study drafts cache
    };
  }, []);

  // Helper to update local state from localStorage items
  const updateCurrentIds = () => {
    const studyId = localStorage.getItem('currentStudyId');
    const draftId = localStorage.getItem('currentStudyDraftId');

    if (studyId) {
      const numericId = parseInt(studyId, 10);
      if (!isNaN(numericId)) {
        setCurrentStudyId(numericId);
      }
    }

    if (draftId) {
      const numericId = parseInt(draftId, 10);
      if (!isNaN(numericId)) {
        setCurrentStudyDraftId(numericId);
      }
    }
  };

  // Fetch standard surveys for the studyName, store in `surveys`
  const fetchSurveys = async () => {
    try {
      setLoading(prev => ({ ...prev, surveys: true }));

      if (!studyName) {
        setSurveys([]);
        return;
      }
      const response = await surveyService.getSurveysByStudyId(studyName, 'standard');
      if (response && response.surveys) {
        const standardSurveys = response.surveys.filter(s => s.survey_type === 'standard');
        setSurveys(standardSurveys);
      } else {
        setSurveys([]);
      }
    } catch (err) {
      setError(prev => ({ ...prev, surveys: 'Failed to load surveys' }));
      console.error('Error fetching surveys:', err);
    } finally {
      setLoading(prev => ({ ...prev, surveys: false }));
    }
  };

  // Fetch missing forms for the studyName, store in `missingForms`
  const fetchMissingForms = async () => {
    try {
      setLoading(prev => ({ ...prev, missingForms: true }));

      if (!studyName) {
        setMissingForms([]);
        return;
      }
      const response = await surveyService.getSurveysByStudyId(studyName, 'missing_form');
      if (response && response.surveys) {
        const missingFormSurveys = response.surveys.filter(s => s.survey_type === 'missing_form');
        setMissingForms(missingFormSurveys);
      } else {
        setMissingForms([]);
      }
    } catch (err) {
      setError(prev => ({ ...prev, missingForms: 'Failed to load missing forms' }));
      console.error('Error fetching missing forms:', err);
    } finally {
      setLoading(prev => ({ ...prev, missingForms: false }));
    }
  };

  // Fetch reminders for the current draft, store in `emailReminders` / `pushReminders`
  const fetchReminders = async () => {
    try {
      setLoading(prev => ({ ...prev, emailReminders: true, pushReminders: true }));

      if (!currentStudyDraftId) {
        setEmailReminders([]);
        setPushReminders([]);
        return;
      }

      // Clear the cache for reminders before fetching
      const cacheKey = `reminders_${currentStudyDraftId}`;
      localStorage.removeItem(cacheKey);
      
      const response = await reminderService.getReminderTemplates(currentStudyDraftId);
      
      // Handle paginated response structure
      const reminders = response.results || response;
      
      // Ensure we have an array and filter out any invalid items
      const validReminders = Array.isArray(reminders) ? reminders : [];
      
      console.log('Fetched reminders:', validReminders);
      
      setEmailReminders(validReminders.filter(r => r && r.channel === 'email'));
      setPushReminders(validReminders.filter(r => r && r.channel === 'push'));
    } catch (err) {
      setError(prev => ({
        ...prev,
        emailReminders: 'Failed to load email reminders',
        pushReminders: 'Failed to load push reminders'
      }));
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(prev => ({
        ...prev,
        emailReminders: false,
        pushReminders: false
      }));
    }
  };

  /**
   * Callback to open the modal for a specific type: standard_survey, missing_form,
   * email_reminder, or push_reminder. Also reset any editing states.
   */
  const handleOptionClick = (type) => {
    setSelectedType(type);
    setEditingSurvey(null);
    setEditingReminder(null);
    setModalOpen(true);
  };

  // Closes the modal (called from the modal or the parent)
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedType(null);
    setEditingSurvey(null);
    setEditingReminder(null);
  };

  // Copy an item to clipboard as JSON
  const handleCopyToClipboard = (item, type) => {
    navigator.clipboard
      .writeText(JSON.stringify(item))
      .then(() => {
        toast.success(`${type} copied to clipboard!`, {
          duration: 2000,
          position: 'bottom-right',
          style: {
            background: '#10B981',
            color: '#FFFFFF'
          }
        });
      })
      .catch(() => {
        toast.error(`Failed to copy ${type.toLowerCase()} to clipboard`);
      });
  };

  // Editing an existing Survey triggers the modal with the correct type (standard or missing)
  const handleEditSurvey = (survey) => {
    setSelectedType(survey.is_missing ? 'missing_form' : 'standard_survey');
    setEditingSurvey(survey);
    setModalOpen(true);
  };

  // Deleting a survey triggers the "are you sure?" modal
  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSurveyToDelete(null);
  };

  // Once user confirms deletion, call the API, refresh lists, close modal
  const handleDeleteConfirm = async () => {
    try {
      await surveyService.deleteSurvey(surveyToDelete.id);
      toast.success('Survey deleted successfully');
      fetchSurveys();
      fetchMissingForms();
      setDeleteModalOpen(false);
      setSurveyToDelete(null);
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error('Failed to delete survey');
    }
  };

  // Edit or delete a Reminder item (similar pattern to surveys)
  const handleReminderEdit = (e, reminder) => {
    e.stopPropagation();
    const reminderType = reminder.channel === 'email' ? 'email_reminder' : 'push_notification';
    setSelectedType(reminderType);
    setEditingReminder(reminder);
    setModalOpen(true);
  };

  const handleReminderDeleteClick = (e, reminder) => {
    e.stopPropagation();
    setReminderToDelete(reminder);
    setDeleteReminderModalOpen(true);
  };

  const handleDeleteReminderCancel = () => {
    setDeleteReminderModalOpen(false);
    setReminderToDelete(null);
  };

  const handleDeleteReminderConfirm = async () => {
    try {
      await reminderService.deleteReminderTemplate(reminderToDelete.id);
      toast.success('Reminder template deleted successfully');
      fetchReminders();
      setDeleteReminderModalOpen(false);
      setReminderToDelete(null);
    } catch (error) {
      console.error('Error deleting reminder template:', error);
      toast.error('Failed to delete reminder template');
    }
  };

  // Toggle expansions for the standard/missing surveys or reminders
  const toggleStandardSurveyExpansion = (surveyId) => {
    setExpandedStandardSurvey(expandedStandardSurvey === surveyId ? null : surveyId);
  };

  const toggleMissingFormExpansion = (surveyId) => {
    setExpandedMissingForm(expandedMissingForm === surveyId ? null : surveyId);
  };

  const toggleReminderExpansion = (reminderId) => {
    setExpandedReminder(expandedReminder === reminderId ? null : reminderId);
  };

  const showDeleteButtons = false;

  // Render snippet for question previews in the expanded details
  const renderQuestionPreview = (question) => {
    if (!question) return null;
    return (
      <div className="ml-6 mt-2">
        <p className="text-gray-700 dark:text-gray-200 font-medium">{question.text}</p>
        <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Type: {question.type}</p>
        {question.options && (
          <div className="mt-1">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Options:</p>
            {question.type === 'rating' ? (
              <div className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                <p>Min: {question.options.min}</p>
                <p>Max: {question.options.max}</p>
                {question.options.labels && (
                  <div>
                    <p>Labels:</p>
                    <ul className="list-disc list-inside">
                      {question.options.labels.map((label, idx) => (
                        <li key={idx}>{label}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : Array.isArray(question.options) && question.options.length > 0 ? (
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 ml-2">
                {question.options.map((option, idx) => (
                  <li key={idx}>{option}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  // Render a standard (non-missing) survey card
  const renderStandardSurveyItem = (survey) => {
    return (
      <motion.div
        key={survey.id}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 mb-4 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Book className="w-5 h-5 text-primary" />
            <div>
              <h3 className="text-lg font-semibold">{survey.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{survey.short_description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleEditSurvey(survey)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
              title="Edit Survey"
            >
              <Edit className="w-5 h-5" />
            </button>
            {/* <button
              onClick={() => handleDeleteClick(survey)}
              className="p-2 text-gray-600 hover:text-red-500 transition-colors"
              title="Delete Survey"
            >
              <Trash2 className="w-5 h-5" />
            </button> */}
            <button
              onClick={() => toggleStandardSurveyExpansion(survey.id)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
            >
              {expandedStandardSurvey === survey.id ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {expandedStandardSurvey === survey.id && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="border-t border-gray-200 dark:border-gray-700"
          >
            <div className="p-4">
              {survey.long_description && (
                <p className="text-gray-700 dark:text-gray-200 mb-4">{survey.long_description}</p>
              )}
              {survey.questions && survey.questions.length > 0 ? (
                <div>
                  <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Questions:</h5>
                  {survey.questions.map((question, index) => (
                    <div key={index} className="mb-4">
                      {renderQuestionPreview(question)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">No questions available</p>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // Render a missing form survey card
  const renderMissingFormItem = (survey) => {
    return (
      <motion.div
        key={survey.id}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 mb-4 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <h3 className="text-lg font-semibold">{survey.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{survey.short_description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleEditSurvey(survey)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
              title="Edit Missing Form"
            >
              <Edit className="w-5 h-5" />
            </button>
            {showDeleteButtons && (
            <button
              onClick={() => handleDeleteClick(survey)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 transition-colors"
              title="Delete Missing Form"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            )}
            <button
              onClick={() => toggleMissingFormExpansion(survey.id)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
            >
              {expandedMissingForm === survey.id ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {expandedMissingForm === survey.id && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="border-t border-gray-200 dark:border-gray-700"
          >
            <div className="p-4">
              {survey.long_description && (
                <p className="text-gray-700 dark:text-gray-200 mb-4">{survey.long_description}</p>
              )}
              {survey.questions && survey.questions.length > 0 ? (
                <div>
                  <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Questions:</h5>
                  {survey.questions.map((question, index) => (
                    <div key={index} className="mb-4">
                      {renderQuestionPreview(question)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">No questions available</p>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // Render a reminder item (email or push) card
  const renderReminderItem = (reminder) => {
    console.log('Rendering reminder:', reminder);
    return (
      <motion.div
        key={reminder.id}
        initial={false}
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      >
        <button
          onClick={() => toggleReminderExpansion(reminder.id)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">{reminder.name}</h4>
              <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                {reminder.channel === 'email' ? 'Email' : 'Push'}
              </span>
            </div>
            {reminder.channel === 'email' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-left">
                <span className="font-medium">Subject:</span> {reminder.metadata?.subject || 'No subject'}
              </p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{reminder.template_content}</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => handleReminderEdit(e, reminder)}
              className="p-2 text-gray-400 hover:text-primary transition-colors"
              title="Edit Reminder Template"
            >
              <Edit className="w-5 h-5" />
            </button>
            {showDeleteButtons && (<button
              onClick={(e) => handleReminderDeleteClick(e, reminder)}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete Reminder Template"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            )}
            {expandedReminder === reminder.id ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {expandedReminder === reminder.id && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm dark:shadow-gray-900/20">
                <h5 className="font-medium mb-2">Template Content</h5>
                {reminder.channel === 'email' && (
                  <div className="mb-3 text-left">
                    <span className="font-medium text-gray-700 dark:text-gray-200">Subject: </span>
                    <span className="text-gray-600 dark:text-gray-400">{reminder.metadata?.subject || 'No subject'}</span>
                  </div>
                )}
                <div
                  className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 p-3 rounded"
                  dangerouslySetInnerHTML={{
                    __html: parseMarkdown(reminder.template_content)
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  // A helper to render each library section in a consistent manner
  const renderContentSection = (title, items, loadingFlag, errorMsg, emptyMessage, renderFunc) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mt-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 overflow-hidden"
    >
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      </div>

      <div className="p-6">
        {loadingFlag ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading {title.toLowerCase()}...</p>
          </div>
        ) : errorMsg ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md">{errorMsg}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">{emptyMessage}</div>
        ) : (
          <div className="space-y-4">{items.map(renderFunc)}</div>
        )}
      </div>
    </motion.div>
  );

  // Confirmation modal for deleting a Survey
  const DeleteConfirmationModal = () => (
    <AnimatePresence>
      {deleteModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 w-full max-w-md overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete Survey</h3>
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete "{surveyToDelete?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Survey
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Confirmation modal for deleting a Reminder
  const DeleteReminderConfirmationModal = () => (
    <AnimatePresence>
      {deleteReminderModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 w-full max-w-md overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete Reminder</h3>
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete "{reminderToDelete?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteReminderCancel}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteReminderConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Reminder
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /**
   * A function we pass into the modal so it can refresh the lists after a new item is created or updated.
   */
  const refreshLibraryItems = () => {
    console.log('Refreshing library items...');
    fetchSurveys();
    fetchMissingForms();
    fetchReminders();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to={`/studybuilder/${encodeURIComponent(studyName)}`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study Options</span>
          </Link>
        </motion.div>

        {/* Create Library Item Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 overflow-hidden"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">Create Library Item</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              <motion.button
                onClick={() => handleOptionClick('standard_survey')}
                className="flex items-center justify-center p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-200 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-all duration-200 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-center">
                  <div className="bg-primary-50 p-3 rounded-full inline-block mb-4">
                    <Book className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
                    Create Survey
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Build a new standard survey</p>
                </div>
              </motion.button>

              <motion.button
                onClick={() => handleOptionClick('missing_form')}
                className="flex items-center justify-center p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-amber-200 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-all duration-200 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-center">
                  <div className="bg-amber-50 p-3 rounded-full inline-block mb-4">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-amber-600 transition-colors">
                    Create Missing Form
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Create a form for missing assessments</p>
                </div>
              </motion.button>

              <motion.button
                onClick={() => handleOptionClick('email_reminder')}
                className="flex items-center justify-center p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-200 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-all duration-200 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-center">
                  <div className="bg-blue-50 p-3 rounded-full inline-block mb-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">
                    Create Email Reminder
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Set up email notification templates
                  </p>
                </div>
              </motion.button>

              <motion.button
                onClick={() => handleOptionClick('push_notification')}
                className="flex items-center justify-center p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-200 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-all duration-200 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-center">
                  <div className="bg-purple-50 p-3 rounded-full inline-block mb-4">
                    <MessageSquare className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-purple-600 transition-colors">
                    Create Push Notification
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Set up push notification templates
                  </p>
                </div>
              </motion.button>

              {/* Import from Global Library Card */}
              <motion.button
                onClick={() => setImportModalOpen(true)}
                className="flex items-center justify-center p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-green-200 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-all duration-200 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="text-center">
                  <div className="bg-green-50 p-3 rounded-full inline-block mb-4">
                    <Library className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 group-hover:text-green-600 transition-colors">
                    Import from Global Library
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Browse and import from global library
                  </p>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Renders each content section (surveys, missing, email reminders, push reminders) */}
        {renderContentSection(
          'Standard Surveys',
          surveys,
          loading.surveys,
          error.surveys,
          'No standard surveys created yet',
          renderStandardSurveyItem
        )}
        {renderContentSection(
          'Missing Forms',
          missingForms,
          loading.missingForms,
          error.missingForms,
          'No missing forms created yet',
          renderMissingFormItem
        )}

        {console.log('Email reminders before rendering:', emailReminders)}
        {renderContentSection(
          'Email Reminders',
          emailReminders,
          loading.emailReminders,
          error.emailReminders,
          'No email reminders created yet',
          renderReminderItem
        )}

        {console.log('Push reminders before rendering:', pushReminders)}
        {renderContentSection(
          'Push Notifications',
          pushReminders,
          loading.pushReminders,
          error.pushReminders,
          'No push notifications created yet',
          renderReminderItem
        )}

        {/* Could also show loading/error for "studyDraft" if needed */}
        {loading.studyDraft ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading study draft...</p>
          </div>
        ) : error.studyDraft ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md">{error.studyDraft}</div>
        ) : (
          // Render the LibraryBuilderModal if no draft error/ still no studyDraft loading
          <LibraryBuilderModal
            isOpen={modalOpen}
            onClose={handleModalClose}
            type={selectedType}
            currentStudyId={currentStudyId}
            currentStudyDraftId={currentStudyDraftId}
            studyName={studyName}
            editingSurvey={editingSurvey}
            editingReminder={editingReminder}

            // <-- PASS REFRESH CALLBACK HERE!!
            onLibraryUpdated={() => {
              // re-fetch all lists after create or update, no manual refresh needed:
              fetchSurveys();
              fetchMissingForms();
              fetchReminders();
            }}
          />
        )}
      </div>

      {/* Delete confirmations for Surveys and Reminders */}
      <DeleteConfirmationModal />
      <DeleteReminderConfirmationModal />

      {/* Import Library Modal */}
      <AnimatePresence>
        {importModalOpen && (
          <LibraryImportModal
            isOpen={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            studyName={studyName}
            currentStudyId={currentStudyId}
            currentStudyDraftId={currentStudyDraftId}
            onImportComplete={refreshLibraryItems}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudyLibraryDashboard;